import { useEffect, useState } from "react";

// Shared half-height state for the AI Assistant chevron toggle. Module-
// level so the choice (full vs. half viewport) survives page navigations
// within the SPA, and mirrored to localStorage so a refresh / new test
// keeps whatever the user last picked.
//
// Default: true (= half / "short"). New users start with a condensed
// chat by default. Explicit choice — either way — is what's written to
// localStorage, so the missing-key case means "default."

const STORAGE_KEY = "testcraft.chat.halfHeight";
const DEFAULT_VALUE = true;

function readInitial(): boolean {
  try {
    if (typeof window === "undefined") return DEFAULT_VALUE;
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "1") return true;
    if (stored === "0") return false;
    return DEFAULT_VALUE;
  } catch {
    return DEFAULT_VALUE;
  }
}

let sharedHalfHeight = readInitial();
const listeners = new Set<(value: boolean) => void>();

function setShared(next: boolean) {
  sharedHalfHeight = next;
  try {
    window.localStorage.setItem(STORAGE_KEY, next ? "1" : "0");
  } catch {
    /* private mode */
  }
  listeners.forEach((listener) => listener(next));
}

/** Reset to the default (true). Called from TestContext.resetTest() so a
 *  new test starts with a condensed assistant regardless of the user's
 *  prior session preference. */
export function resetChatHalfHeight() {
  setShared(DEFAULT_VALUE);
}

export function useChatHalfHeight(): [boolean, (next: boolean) => void] {
  const [value, setValue] = useState(sharedHalfHeight);

  useEffect(() => {
    listeners.add(setValue);
    setValue(sharedHalfHeight);
    return () => {
      listeners.delete(setValue);
    };
  }, []);

  return [value, setShared];
}
